import { gatewayApi as api } from './gatewayApi';

// Types
export interface PurchaseOrder {
  id?: string;
  Number: string;
  PoDate: string;
  DeliveryDate: string;
  OrderQty: number;
  UoMId: string;
  PricePerUnit: number;
  ClientId: string;
  PoStatusId: number;
  DeliveryAddressStreet: string;
  DeliveryZIP: string;
  DeliveryCity: string;
  State: string;
  Country: string;
  Serials?: Serial[];
  Documents?: Document[];
  Client?: Client;
  POStatus?: POStatus;
  UoM?: UoM;
}

export interface Serial {
  id: string;
  SerialNum: string;
  PartNumber: string;
  PartDescription: string;
  PartPOId: string;
}

export interface Document {
  id: string;
  Name: string;
  DocumentNum: string;
  Description: string;
  Version: string;
}

export interface Client {
  id: string;
  Name: string;
  Code: string;
}

export interface POStatus {
  id: number;
  Name: string;
  Color?: string;
}

export interface UoM {
  id: string;
  Name: string;
  Code: string;
}

export interface PurchaseOrderResponse {
  data: PurchaseOrder[];
  total: number;
  page: number;
  limit: number;
}

// Transform frontend data to backend format
const transformToBackendFormat = (po: PurchaseOrder) => ({
  Number: po.Number,
  PoStatusId: parseInt(po.PoStatusId.toString()),
  PoDate: po.PoDate,
  DeliveryDate: po.DeliveryDate,
  OrderQty: parseInt(po.OrderQty.toString()),
  ClientId: parseInt(po.ClientId),
  UoMId: parseInt(po.UoMId),
  DeliveryAddressStreet: po.DeliveryAddressStreet,
  State: po.State,
  DeliveryCity: po.DeliveryCity,
  DeliveryZIP: po.DeliveryZIP,
  Country: po.Country,
  serialData: po.Serials?.map(serial => ({
    PartId: parseInt(serial.PartPOId),
    SerialNum: serial.SerialNum
  })) || [],
  documentData: po.Documents?.map(doc => ({
    Name: doc.Name,
    DocumentNum: doc.DocumentNum,
    Description: doc.Description,
    Version: doc.Version
  })) || []
});

// Transform backend data to frontend format
const transformFromBackendFormat = (backendPO: any): PurchaseOrder => ({
  id: backendPO.Id?.toString(),
  Number: backendPO.Number,
  PoDate: backendPO.PoDate,
  DeliveryDate: backendPO.DeliveryDate,
  OrderQty: backendPO.OrderQty,
  UoMId: backendPO.UoMId?.toString(),
  PricePerUnit: backendPO.PricePerUnit || 0,
  ClientId: backendPO.ClientId?.toString(),
  PoStatusId: backendPO.PoStatusId,
  DeliveryAddressStreet: backendPO.DeliveryAddressStreet,
  DeliveryZIP: backendPO.DeliveryZIP,
  DeliveryCity: backendPO.DeliveryCity,
  State: backendPO.State,
  Country: backendPO.Country,
  Serials: backendPO.Serials || [],
  Documents: backendPO.Documents || [],
  Client: backendPO.Client ? {
    id: backendPO.Client.Id?.toString(),
    Name: backendPO.Client.Name,
    Code: backendPO.Client.Code || ''
  } : undefined,
  POStatus: backendPO.POStatus ? {
    id: backendPO.POStatus.Id,
    Name: backendPO.POStatus.Name,
    Color: backendPO.POStatus.Color
  } : undefined,
  UoM: backendPO.UoM ? {
    id: backendPO.UoM.Id?.toString(),
    Name: backendPO.UoM.Name,
    Code: backendPO.UoM.Code || ''
  } : undefined
});

// Purchase Order API Service
export const purchaseOrderApi = {
  // Get all purchase orders
  getAll: (params?: { page?: number; limit?: number; search?: string }): Promise<PurchaseOrderResponse> =>
    api.get<PurchaseOrderResponse>('/purchaseorder', params).then(response => ({
      ...response,
      data: response.data?.map(transformFromBackendFormat) || []
    })),

  // Get purchase order by ID
  getById: (id: string): Promise<PurchaseOrder> =>
    api.get<{ data: any }>(`/purchaseorder/${id}`).then(response => transformFromBackendFormat(response.data)),

  // Create new purchase order
  create: (purchaseOrder: Omit<PurchaseOrder, 'id'>): Promise<PurchaseOrder> =>
    api.post<{ data: any }>('/purchaseorder', { data: transformToBackendFormat(purchaseOrder) }).then(response => transformFromBackendFormat(response.data)),

  // Update purchase order
  update: (id: string, purchaseOrder: Partial<PurchaseOrder>): Promise<PurchaseOrder> =>
    api.put<{ data: any }>('/purchaseorder', { 
      id: parseInt(id), 
      data: transformToBackendFormat(purchaseOrder as PurchaseOrder) 
    }).then(response => transformFromBackendFormat(response.data)),

  // Delete purchase order
  delete: (id: string): Promise<void> =>
    api.delete<void>(`/purchaseorder/${id}`),

  // Search purchase orders
  search: (field: string, value: string): Promise<PurchaseOrder[]> =>
    api.get<PurchaseOrder[]>(`/purchaseorder/search/${field}/${value}`),

  // Get purchase order serials
  getSerials: (purchaseOrderId: string): Promise<Serial[]> =>
    api.get<Serial[]>(`/purchaseorder/${purchaseOrderId}/serials`),

  // Add serial to purchase order
  addSerial: (purchaseOrderId: string, serial: Omit<Serial, 'id'>): Promise<Serial> =>
    api.post<Serial>(`/purchaseorder/${purchaseOrderId}/serials`, serial),

  // Delete serial from purchase order
  deleteSerial: (purchaseOrderId: string, serialId: string): Promise<void> =>
    api.delete<void>(`/purchaseorder/serial/${serialId}`),

  // Get purchase order documents
  getDocuments: (purchaseOrderId: string): Promise<Document[]> =>
    api.get<Document[]>(`/purchaseorder/${purchaseOrderId}/documents`),

  // Upload document
  uploadDocument: (purchaseOrderId: string, formData: FormData): Promise<Document> =>
    api.upload<Document>(`/purchaseorder/${purchaseOrderId}/documents`, formData),

  // Delete document
  deleteDocument: (purchaseOrderId: string, documentId: string): Promise<void> =>
    api.delete<void>(`/purchaseorder/${purchaseOrderId}/documents/${documentId}`),

  // Close purchase order
  close: (id: string, comment?: string): Promise<PurchaseOrder> =>
    api.put<PurchaseOrder>('/purchaseorder/close', { id, comment }),

  // Approve purchase order
  approve: (id: string, comment?: string): Promise<PurchaseOrder> =>
    api.put<PurchaseOrder>('/purchaseorder/approve', { id, comment }),

  // Reject purchase order
  reject: (id: string, comment?: string): Promise<PurchaseOrder> =>
    api.put<PurchaseOrder>('/purchaseorder/reject', { id, comment }),

  // Get purchase order status
  getStatus: (id: string): Promise<{ status: string; comment?: string }> =>
    api.get<{ status: string; comment?: string }>(`/purchaseorder/${id}/status`),

  // Get available clients
  getClients: (): Promise<Client[]> =>
    api.get<Client[]>('/client'),

  // Get available UoMs
  getUoMs: (): Promise<UoM[]> =>
    api.get<UoM[]>('/uom'),

  // Get available PO statuses
  getPOStatuses: (): Promise<POStatus[]> =>
    api.get<POStatus[]>('/postatus'),
}; 